import { IIndirectTaxFilingV2WithAssociations } from '@commenda/api/indirect-tax/filings/types';
import { SalesTaxFilingStatusEnum } from '@prisma/client';
import { State } from 'country-state-city';

import { IndirectTaxRegion, IndirectTaxRegionHelper } from '../../common';
import { IndirectTaxFilingAccessHelper } from '../../common/helpers/IndirectTaxFilingAccessHelper';
import { IIndirectTaxFiling } from '../types';

import {
  IndirectTaxFilingReturnSummaryHelper,
  TAXABLE_SALES_SUMMARY_BUCKET,
} from './IndirectTaxFilingReturnSummaryHelper';
import { IndirectTaxFilingsHelper } from './IndirectTaxFilingsHelper';

export class IndirectTaxFilingListRowHelper {
  // Common fields every region emits the same way (name, period, status,
  // jurisdiction formatting). Factored out so per-region helpers only need
  // to supply what's actually region-specific.
  private static buildListRowBase(
    filing: IIndirectTaxFilingV2WithAssociations,
    region: IndirectTaxRegion
  ): Pick<
    IIndirectTaxFiling,
    | 'id'
    | 'returnName'
    | 'status'
    | 'jurisdiction'
    | 'countryCode'
    | 'filingPeriod'
    | 'filingFrequency'
    | 'dueDate'
    | 'currency'
    | 'region'
    | 'canApprove'
    | 'canReview'
    | 'filedByAgentName'
  > {
    const countryCode =
      IndirectTaxRegionHelper.deriveCountryCodeFromJurisdiction({
        jurisdictionType: filing.jurisdictionType,
        jurisdictionName: filing.jurisdictionName,
        country: filing.country,
      });
    // The current detail page also receives older filings without jurisdiction_* fields.
    const jurisdictionName =
      filing.jurisdictionName ||
      State.getStateByCodeAndCountry(filing.state ?? '', countryCode)?.name ||
      filing.state;

    return {
      id: filing.filingId,
      returnName: filing.formTitle,
      status: IndirectTaxFilingsHelper.mapApiStatus(filing.status),
      jurisdiction:
        IndirectTaxFilingsHelper.formatJurisdictionName(jurisdictionName),
      countryCode,
      filingPeriod: IndirectTaxFilingsHelper.formatFilingPeriod(
        filing.reportingPeriodStartDate,
        filing.filingFrequency
      ),
      filingFrequency: IndirectTaxFilingsHelper.getFilingFrequencyName(
        filing.filingFrequency
      ),
      dueDate: IndirectTaxFilingsHelper.formatDueDate(filing.filingDueDate),
      currency: IndirectTaxFilingsHelper.getFilingCurrency(filing, region),
      region,
      canApprove:
        IndirectTaxRegionHelper.supportsFilingWorkflow(region) &&
        IndirectTaxFilingsHelper.canApproveFiling(filing.serviceRequest),
      canReview:
        countryCode === 'US' &&
        IndirectTaxFilingsHelper.canReviewFiling(filing.serviceRequest),
      filedByAgentName: filing.filedByAgent?.name ?? null,
    };
  }

  // US list row — amount + US-specific breakdown fields (taxable / non-taxable
  // / exempt / marketplace sales + tax due). Reads actual values on FILED,
  // estimated values otherwise. All numbers in major currency units already.
  static toListRowForUS(
    filing: IIndirectTaxFilingV2WithAssociations
  ): IIndirectTaxFiling {
    const region = IndirectTaxRegion.UNITED_STATES;
    const isFiled = filing.status === SalesTaxFilingStatusEnum.FILED;
    return {
      ...IndirectTaxFilingListRowHelper.buildListRowBase(filing, region),
      amount: (isFiled ? filing.taxPaid : filing.estimatedTaxCollected) ?? 0,
      taxableSales:
        (isFiled ? filing.taxableAmount : filing.estimatedTaxableAmount) ??
        undefined,
      nonTaxableSales:
        (isFiled
          ? filing.nonTaxableAmount
          : filing.estimatedNonTaxableAmount) ?? undefined,
      exemptSales:
        (isFiled ? filing.exemptAmount : filing.estimatedExemptAmount) ??
        undefined,
      taxDue:
        (isFiled ? filing.taxPaid : filing.estimatedTaxCollected) ?? undefined,
    };
  }

  // The list renders `N/A` while this is `false`; regions that don't hide
  // amounts behind the agent's report step leave it undefined.
  private static resolveReportGate(
    filing: IIndirectTaxFilingV2WithAssociations,
    region: IndirectTaxRegion
  ): boolean | undefined {
    if (!IndirectTaxRegionHelper.hidesAmountsUntilReportGenerated(region)) {
      return undefined;
    }
    return IndirectTaxFilingsHelper.hasGeneratedFilingReportAtLeastOnce(
      filing.serviceRequest
    );
  }

  // VAT list row (EU member states + UK). `estimatedTaxCollected` is 0 by
  // contract for EU/UK filings, so VAT position and amount-due are derived
  // from contributions (final FORMULA per jurisdiction — see
  // `computeVatAmountFromContributions`) with `taxLiability` as an override
  // when the backend has posted one. `taxablePurchases` / intra-community
  // fields stay undefined until STA2 starts emitting them.
  static toListRowForVAT(
    filing: IIndirectTaxFilingV2WithAssociations,
    region: IndirectTaxRegion.EUROPEAN_UNION | IndirectTaxRegion.UNITED_KINGDOM
  ): IIndirectTaxFiling {
    const vatPosition =
      filing.taxLiability ??
      IndirectTaxFilingReturnSummaryHelper.computeVatAmountFromContributions(
        filing.contributions ?? []
      );
    return {
      ...IndirectTaxFilingListRowHelper.buildListRowBase(filing, region),
      amount: vatPosition ?? 0,
      hasGeneratedFilingReport:
        IndirectTaxFilingListRowHelper.resolveReportGate(filing, region),
      taxableSales:
        filing.taxableAmount ?? filing.estimatedTaxableAmount ?? undefined,
      vatPosition: vatPosition ?? undefined,
      taxDue: filing.taxPaid ?? vatPosition ?? undefined,
    };
  }

  // Supports both flat-field and contribution-driven Canada filings.
  static toListRowForCanada(
    filing: IIndirectTaxFilingV2WithAssociations
  ): IIndirectTaxFiling {
    const base = IndirectTaxFilingListRowHelper.buildListRowBase(
      filing,
      IndirectTaxRegion.CANADA
    );
    if (
      IndirectTaxRegionHelper.isContributionRegion(IndirectTaxRegion.CANADA)
    ) {
      const contributions = filing.contributions ?? [];
      const taxPosition =
        filing.taxLiability ??
        IndirectTaxFilingReturnSummaryHelper.resolveVatPositionFromContributions(
          contributions
        ) ??
        IndirectTaxFilingReturnSummaryHelper.computeVatAmountFromContributions(
          contributions
        );
      return {
        ...base,
        amount: taxPosition ?? 0,
        hasGeneratedFilingReport:
          IndirectTaxFilingListRowHelper.resolveReportGate(
            filing,
            IndirectTaxRegion.CANADA
          ),
        taxableSales:
          IndirectTaxFilingReturnSummaryHelper.resolveSummaryBucketFromContributions(
            contributions,
            TAXABLE_SALES_SUMMARY_BUCKET
          ) ?? undefined,
        vatPosition: taxPosition ?? undefined,
        taxDue: filing.taxPaid ?? taxPosition ?? undefined,
      };
    }
    return {
      ...base,
      amount: filing.taxLiability ?? filing.estimatedTaxCollected ?? 0,
      taxableSales:
        filing.taxableAmount ?? filing.estimatedTaxableAmount ?? undefined,
      exemptSales:
        filing.exemptAmount ?? filing.estimatedExemptAmount ?? undefined,
      vatPosition: filing.taxLiability ?? undefined,
      taxDue: filing.taxPaid ?? filing.estimatedTaxCollected ?? undefined,
    };
  }

  // Dispatch to the right per-region list-row builder. Callers that already
  // know the region (dashboard hook does — it has an activeRegion) can skip
  // this and call the specific helper directly.
  static toListRow(
    filing: IIndirectTaxFilingV2WithAssociations
  ): IIndirectTaxFiling {
    const region = IndirectTaxRegionHelper.deriveRegionFromJurisdiction({
      jurisdictionType: filing.jurisdictionType,
      jurisdictionName: filing.jurisdictionName,
      country: filing.country,
    });
    if (
      region === IndirectTaxRegion.EUROPEAN_UNION ||
      region === IndirectTaxRegion.UNITED_KINGDOM
    ) {
      return IndirectTaxFilingListRowHelper.toListRowForVAT(filing, region);
    }
    if (region === IndirectTaxRegion.CANADA) {
      return IndirectTaxFilingListRowHelper.toListRowForCanada(filing);
    }
    if (region === IndirectTaxRegion.UNITED_STATES) {
      return IndirectTaxFilingListRowHelper.toListRowForUS(filing);
    }
    return IndirectTaxFilingListRowHelper.toCalendarListRow(filing, region);
  }

  static toListRowForViewer(
    filing: IIndirectTaxFilingV2WithAssociations,
    isInternalUser: boolean
  ): IIndirectTaxFiling {
    const region = IndirectTaxRegionHelper.deriveRegionFromJurisdiction(filing);
    return IndirectTaxFilingAccessHelper.canViewDetails(region, isInternalUser)
      ? this.toListRow(filing)
      : this.toCalendarListRow(filing, region);
  }

  static toCalendarListRow(
    filing: IIndirectTaxFilingV2WithAssociations,
    region: IndirectTaxRegion
  ): IIndirectTaxFiling {
    return {
      ...IndirectTaxFilingListRowHelper.buildListRowBase(filing, region),
      amount: null,
      canApprove: false,
      canReview: false,
    };
  }
}
